import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/*
 * Botones de Facebook, TikTok e Instagram.
 * Los enlaces salen de Admin > Informacion (/api/informacion/). Si la API falla,
 * se usan los de abajo. Si el cliente deja un campo vacio en el admin, ese boton no aparece.
 */

export type Redes = { facebook: string; tiktok: string; instagram: string };

const RESPALDO: Redes = {
  facebook: 'https://www.facebook.com/ticobaterias',
  tiktok: 'https://www.tiktok.com/@ticobaterias',
  instagram: 'https://www.instagram.com/ticobaterias/',
};

let cache: Promise<Redes> | null = null;

function cargarRedes(): Promise<Redes> {
  if (!cache) {
    cache = fetch('/api/informacion/')
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((d: any) => ({
        facebook: (d.facebook || '').trim(),
        tiktok: (d.tiktok || '').trim(),
        instagram: (d.instagram || '').trim(),
      }))
      .catch(() => RESPALDO);
  }
  return cache;
}

export function useRedes(): Redes {
  const [redes, setRedes] = useState<Redes>(RESPALDO);
  useEffect(() => {
    let vivo = true;
    cargarRedes().then(r => { if (vivo) setRedes(r); });
    return () => { vivo = false; };
  }, []);
  return redes;
}

const ICONOS = {
  facebook: (
    <svg viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M14 8.5V6.8c0-.8.5-1 .9-1H17V2.1L14.1 2C10.9 2 10.2 4.4 10.2 6v2.5H8v3.8h2.2V22H14v-9.7h2.7l.4-3.8H14z"/></svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path transform="translate(-0.7 0.6)" fill="#25F4EE" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
      <path transform="translate(0.7 -0.4)" fill="#FE2C55" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
      <path fill="white" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="white" stroke="none"/></svg>
  ),
};

const FONDOS: Record<keyof Redes, CSSProperties> = {
  facebook: { background: '#1877F2' },
  tiktok: { background: '#000000' },
  instagram: { background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' },
};

const NOMBRES: Record<keyof Redes, string> = {
  facebook: 'Facebook',
  tiktok: 'TikTok',
  instagram: 'Instagram',
};

const ORDEN: (keyof Redes)[] = ['facebook', 'tiktok', 'instagram'];

type Props = {
  tamano?: number;          // diametro del boton en px
  className?: string;       // clases del contenedor
  conTexto?: boolean;       // muestra el nombre de la red (pagina Mercadeo)
};

export default function RedesSociales({ tamano = 32, className = 'flex items-center gap-2', conTexto = false }: Props) {
  const redes = useRedes();
  const icono = Math.round(tamano * 0.55);

  return (
    <div className={className}>
      {ORDEN.filter(red => redes[red]).map(red => (
        <a
          key={red}
          href={redes[red]}
          target="_blank"
          rel="noreferrer noopener"
          title={`TicoBaterías en ${NOMBRES[red]}`}
          aria-label={`TicoBaterías en ${NOMBRES[red]}`}
          className={`group inline-flex items-center gap-2 rounded-full shadow-md ring-2 ring-white/25 hover:ring-white/70 hover:-translate-y-0.5 hover:scale-110 transition-all duration-200 ${conTexto ? 'pr-4' : ''}`}
          style={FONDOS[red]}
        >
          <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: tamano, height: tamano }}>
            <span style={{ width: icono, height: icono, display: 'inline-flex' }}>{ICONOS[red]}</span>
          </span>
          {conTexto && <span className="text-white text-sm font-bold">{NOMBRES[red]}</span>}
        </a>
      ))}
    </div>
  );
}
